import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { getTemplates, deleteTemplate, searchTemplates } from '../../../utils/estimateStorage';

const TemplateLibrary = ({ isOpen, onClose, onLoadTemplate }) => {
  const [templates, setTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTemplates, setFilteredTemplates] = useState([]);

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  useEffect(() => {
    setFilteredTemplates(searchTemplates(searchQuery));
  }, [searchQuery, templates]);

  const loadTemplates = () => {
    const data = getTemplates();
    setTemplates(data);
    setFilteredTemplates(data);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      deleteTemplate(id);
      loadTemplates();
    }
  };

  const handleLoad = (template) => {
    onLoadTemplate(template);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] flex flex-col animate-scale-in">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-accent to-primary shadow-lg">
              <Icon name="FolderOpen" size={20} color="white" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">Template Library</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-smooth"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="p-6 border-b border-border">
          <Input
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            iconName="Search"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {filteredTemplates?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <div className="p-6 rounded-2xl bg-gradient-to-br from-muted to-muted/50 shadow-inner-lg mb-4">
                <Icon name="FolderOpen" size={48} color="var(--color-muted-foreground)" />
              </div>
              <h4 className="text-lg font-semibold text-foreground">No Templates Found</h4>
              <p className="text-sm text-muted-foreground mt-2 max-w-md">
                {searchQuery ? 'Try a different search term' : 'Save your first template to quickly reuse configurations'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredTemplates?.map((template) => (
                <div
                  key={template?.id}
                  className="bg-gradient-to-br from-card to-muted/10 border border-border rounded-xl p-4 hover:shadow-lg transition-all duration-smooth"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-foreground mb-1">{template?.name}</h4>
                      {template?.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2">{template?.description}</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDelete(template?.id)}
                      className="p-1 hover:bg-destructive/10 rounded transition-smooth"
                    >
                      <Icon name="Trash2" size={16} color="var(--color-destructive)" />
                    </button>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name="Truck" size={14} />
                      <span>Mode: {template?.config?.deliveryMode}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name="Users" size={14} />
                      <span>{template?.config?.selectedEngineers?.length} Engineers</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name="Settings" size={14} />
                      <span>{template?.config?.config?.overhead}% / {template?.config?.config?.targetMargin}% / {template?.config?.config?.contingency}%</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    <span className="text-xs text-muted-foreground">
                      {new Date(template?.createdAt)?.toLocaleDateString()}
                    </span>
                    <Button
                      variant="default"
                      size="sm"
                      iconName="Download"
                      iconPosition="left"
                      onClick={() => handleLoad(template)}
                    >
                      Load
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TemplateLibrary;